import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { useInstanceEventStream, useInstanceEvents, useSearchInstanceEvents } from '../hooks/useInstances';
import { InstanceEventType, InstanceStatus, type InstanceEvent } from '../types/instance';

interface InstanceTerminalProps {
  instanceId: string;
  status: InstanceStatus;
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max) + '...';
}

function highlightText(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="rounded bg-yellow-400/40 text-yellow-200">{part}</mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

function EventLine({ event, searchQuery }: { event: InstanceEvent; searchQuery: string }) {
  const time = formatTime(event.createdAt);

  switch (event.eventType) {
    case InstanceEventType.MESSAGE:
      return (
        <div className="flex gap-2">
          <span className="shrink-0 text-slate-500">{time}</span>
          <span className="text-white">
            <span className="mr-1">{'\uD83D\uDCAC'}</span>
            {highlightText(event.content ?? '', searchQuery)}
          </span>
        </div>
      );
    case InstanceEventType.TOOL_USE:
      return (
        <div className="flex gap-2">
          <span className="shrink-0 text-slate-500">{time}</span>
          <span className="text-blue-400">
            <span className="mr-1">{'\uD83D\uDD27'}</span>
            {highlightText(
              `${event.toolName ?? 'tool'}: ${truncate(event.toolInput ? JSON.stringify(event.toolInput) : '', 200)}`,
              searchQuery,
            )}
          </span>
        </div>
      );
    case InstanceEventType.TOOL_RESULT:
      return (
        <div className="flex gap-2">
          <span className="shrink-0 text-slate-500">{time}</span>
          <span className="text-slate-400">
            {highlightText(truncate(event.content ?? '', 300), searchQuery)}
          </span>
        </div>
      );
    case InstanceEventType.ERROR:
      return (
        <div className="flex gap-2">
          <span className="shrink-0 text-slate-500">{time}</span>
          <span className="text-red-400">
            <span className="mr-1">{'\u274C'}</span>
            {highlightText(event.content ?? 'Unknown error', searchQuery)}
          </span>
        </div>
      );
    case InstanceEventType.STATE_CHANGE:
      return (
        <div className="flex gap-2">
          <span className="shrink-0 text-slate-500">{time}</span>
          <span className="text-yellow-400">
            <span className="mr-1">{'\u26A1'}</span>
            {highlightText(event.content ?? '', searchQuery)}
          </span>
        </div>
      );
    case InstanceEventType.PROGRESS:
      return (
        <div className="flex gap-2">
          <span className="shrink-0 text-slate-500">{time}</span>
          <span className="text-slate-500">{highlightText(event.content ?? '', searchQuery)}</span>
        </div>
      );
    default:
      return (
        <div className="flex gap-2">
          <span className="shrink-0 text-slate-500">{time}</span>
          <span className="text-slate-400">{highlightText(event.content ?? '', searchQuery)}</span>
        </div>
      );
  }
}

const EventTypeFilter = {
  ALL: 'all',
  MESSAGES: 'message',
  TOOLS: 'tool_use',
  ERRORS: 'error',
} as const;

type EventTypeFilter = (typeof EventTypeFilter)[keyof typeof EventTypeFilter];

export default function InstanceTerminal({ instanceId, status }: InstanceTerminalProps) {
  const [autoScroll, setAutoScroll] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [eventFilter, setEventFilter] = useState<EventTypeFilter>(EventTypeFilter.ALL);
  const containerRef = useRef<HTMLDivElement>(null);

  const isLive = status === InstanceStatus.RUNNING || status === InstanceStatus.STARTING;
  const { events: streamEvents, isConnected } = useInstanceEventStream(isLive ? instanceId : null);
  const { data: historicalEvents } = useInstanceEvents(!isLive ? instanceId : undefined);
  const { data: searchResults } = useSearchInstanceEvents(
    debouncedQuery ? instanceId : undefined,
    debouncedQuery,
    eventFilter !== EventTypeFilter.ALL ? eventFilter : undefined,
  );

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const baseEvents = isLive ? streamEvents : (historicalEvents ?? []);

  // Apply local filter when not using server search
  const events = useMemo(() => {
    if (debouncedQuery && searchResults) return searchResults;

    let filtered = baseEvents;
    if (eventFilter === EventTypeFilter.MESSAGES) {
      filtered = filtered.filter((e) => e.eventType === InstanceEventType.MESSAGE);
    } else if (eventFilter === EventTypeFilter.TOOLS) {
      filtered = filtered.filter(
        (e) => e.eventType === InstanceEventType.TOOL_USE || e.eventType === InstanceEventType.TOOL_RESULT,
      );
    } else if (eventFilter === EventTypeFilter.ERRORS) {
      filtered = filtered.filter((e) => e.eventType === InstanceEventType.ERROR);
    }
    return filtered;
  }, [baseEvents, searchResults, debouncedQuery, eventFilter]);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [events, autoScroll]);

  const filterButtons: { key: EventTypeFilter; label: string }[] = [
    { key: EventTypeFilter.ALL, label: 'All' },
    { key: EventTypeFilter.MESSAGES, label: 'Messages' },
    { key: EventTypeFilter.TOOLS, label: 'Tools' },
    { key: EventTypeFilter.ERRORS, label: 'Errors' },
  ];

  return (
    <div className="flex flex-col rounded-lg border border-slate-700 bg-slate-900">
      {/* Search & Filter toolbar */}
      <div className="border-b border-slate-700 px-4 py-2">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search events..."
              className="w-full rounded-md border border-slate-700 bg-slate-800 py-1.5 pl-8 pr-8 text-xs text-slate-200 placeholder-slate-500 focus:border-slate-500 focus:outline-none"
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(''); setDebouncedQuery(''); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="flex gap-1">
            {filterButtons.map((btn) => (
              <button
                key={btn.key}
                onClick={() => setEventFilter(btn.key)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  eventFilter === btn.key
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                }`}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-700 px-4 py-2">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className="font-medium text-slate-300">Terminal</span>
          {isLive && (
            <span className={`flex items-center gap-1 ${isConnected ? 'text-green-400' : 'text-yellow-400'}`}>
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${isConnected ? 'bg-green-400' : 'bg-yellow-400'}`} />
              {isConnected ? 'Live' : 'Reconnecting...'}
            </span>
          )}
          {!isLive && (
            <span className="text-slate-500">Historical ({events.length} events)</span>
          )}
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-400">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
            className="rounded border-slate-600"
          />
          Auto-scroll
        </label>
      </div>
      <div
        ref={containerRef}
        className="h-96 overflow-y-auto p-4 font-mono text-xs leading-relaxed"
      >
        {events.length === 0 ? (
          <div className="text-slate-600">
            {debouncedQuery ? 'No matching events.' : isLive ? 'Waiting for events...' : 'No events recorded.'}
          </div>
        ) : (
          <div className="space-y-1">
            {events.map((event) => (
              <EventLine key={event.id} event={event} searchQuery={debouncedQuery} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
